/**
 * 纯前端上传下载管理器
 * 支持进度监听、暂停、继续、取消、断点续传
 */

export interface UploadTask {
  id: string;
  file: File;
  xhr?: XMLHttpRequest;
  startTime: number;
  lastLoaded: number;
  lastTime: number;
  aborted: boolean;
  uploadedBytes: number; // 已上传的字节数（用于断点续传）
}

export interface DownloadTask {
  id: string;
  url: string;
  fileName: string;
  xhr?: XMLHttpRequest;
  startTime: number;
  lastLoaded: number;
  lastTime: number;
  aborted: boolean;
  downloadedBytes: number; // 已下载的字节数（用于断点续传）
  chunks: Blob[]; // 已下载的数据块
}

type ProgressCallback = (loaded: number, total: number, speed: number) => void;
type CompleteCallback = () => void;
type ErrorCallback = (error: Error) => void;
type BlobTransform = (blob: Blob) => Promise<Blob>;

class UploadDownloadManager {
  private uploadTasks: Map<string, UploadTask> = new Map();
  private downloadTasks: Map<string, DownloadTask> = new Map();

  /**
   * 计算实时速度（字节/秒）- 添加节流，避免更新太频繁
   */
  private calculateSpeed(
    task: UploadTask | DownloadTask,
    currentLoaded: number,
  ): number {
    const now = Date.now();
    const timeDiff = (now - task.lastTime) / 1000; // 转换为秒

    // 节流：至少间隔 200ms 才更新速度
    if (timeDiff < 0.2) {
      return 0; // 返回0表示不更新
    }

    const loadedDiff = currentLoaded - task.lastLoaded;
    const speed = loadedDiff / timeDiff;

    // 更新记录
    task.lastLoaded = currentLoaded;
    task.lastTime = now;

    return Math.max(0, speed);
  }

  /**
   * 上传文件（支持进度监听和断点续传）
   */
  uploadFile(
    url: string,
    file: File,
    taskId: string,
    onProgress?: ProgressCallback,
    onComplete?: CompleteCallback,
    onError?: ErrorCallback,
    resumeFrom: number = 0, // 从指定位置继续上传
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const now = Date.now();

      const task: UploadTask = {
        id: taskId,
        file,
        xhr,
        startTime: now,
        lastLoaded: resumeFrom,
        lastTime: now,
        aborted: false,
        uploadedBytes: resumeFrom,
      };

      this.uploadTasks.set(taskId, task);

      // 上传进度监听
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && !task.aborted) {
          const actualLoaded = resumeFrom + e.loaded;
          const speed = this.calculateSpeed(task, actualLoaded);
          // 只有速度不为0时才更新（节流）
          if (speed > 0) {
            onProgress?.(actualLoaded, file.size, speed);
          }
        }
      });

      // 上传完成
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.uploadTasks.delete(taskId);
          onComplete?.();
          resolve();
        } else {
          const error = new Error(`上传失败: ${xhr.status}`);
          this.uploadTasks.delete(taskId);
          onError?.(error);
          reject(error);
        }
      });

      // 上传错误
      xhr.addEventListener('error', () => {
        const error = new Error('网络错误');
        this.uploadTasks.delete(taskId);
        onError?.(error);
        reject(error);
      });

      // 上传中止
      xhr.addEventListener('abort', () => {
        if (!task.aborted) {
          const error = new Error('上传已取消');
          this.uploadTasks.delete(taskId);
          onError?.(error);
          reject(error);
        } else {
          // 正常暂停，抛出错误以中断后续流程
          const error = new Error('上传已暂停');
          reject(error);
        }
      });

      xhr.open('PUT', url);
      xhr.setRequestHeader(
        'Content-Type',
        file.type || 'application/octet-stream',
      );

      // 如果是断点续传，设置 Content-Range 头
      if (resumeFrom > 0) {
        const contentRange = `bytes ${resumeFrom}-${file.size - 1}/${file.size}`;
        xhr.setRequestHeader('Content-Range', contentRange);
        // 只发送剩余部分
        xhr.send(file.slice(resumeFrom));
      } else {
        xhr.send(file);
      }
    });
  }

  /**
   * 下载文件（支持进度监听和断点续传）
   */
  downloadFile(
    url: string,
    fileName: string,
    taskId: string,
    onProgress?: ProgressCallback,
    onComplete?: CompleteCallback,
    onError?: ErrorCallback,
    resumeFrom: number = 0, // 从指定位置继续下载
    existingChunks: Blob[] = [], // 已下载的数据块
    transformBlob?: BlobTransform,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const now = Date.now();

      const task: DownloadTask = {
        id: taskId,
        url,
        fileName,
        xhr,
        startTime: now,
        lastLoaded: resumeFrom,
        lastTime: now,
        aborted: false,
        downloadedBytes: resumeFrom,
        chunks: [...existingChunks],
      };

      this.downloadTasks.set(taskId, task);

      xhr.responseType = 'blob';

      // 下载进度监听
      xhr.addEventListener('progress', (e) => {
        if (e.lengthComputable && !task.aborted) {
          const actualLoaded = resumeFrom + e.loaded;
          const speed = this.calculateSpeed(task, actualLoaded);
          // 只有速度不为0时才更新（节流）
          if (speed > 0) {
            onProgress?.(actualLoaded, resumeFrom + e.total, speed);
          }
        }
      });

      // 下载完成
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          void (async () => {
            try {
              const blob = xhr.response as Blob;
              task.chunks.push(blob);

              const finalBlob = new Blob(task.chunks);
              const outputBlob = transformBlob
                ? await transformBlob(finalBlob)
                : finalBlob;
              const downloadUrl = URL.createObjectURL(outputBlob);
              const a = document.createElement('a');
              a.href = downloadUrl;
              a.download = fileName;
              a.rel = 'noopener';
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(downloadUrl);

              this.downloadTasks.delete(taskId);
              onComplete?.();
              resolve();
            } catch (error: any) {
              const nextError = new Error(error?.message || '下载解密失败');
              this.downloadTasks.delete(taskId);
              onError?.(nextError);
              reject(nextError);
            }
          })();
        } else {
          const error = new Error(`下载失败: ${xhr.status}`);
          this.downloadTasks.delete(taskId);
          onError?.(error);
          reject(error);
        }
      });

      // 下载错误
      xhr.addEventListener('error', () => {
        const error = new Error('网络错误');
        this.downloadTasks.delete(taskId);
        onError?.(error);
        reject(error);
      });

      // 下载中止
      xhr.addEventListener('abort', () => {
        if (!task.aborted) {
          const error = new Error('下载已取消');
          this.downloadTasks.delete(taskId);
          onError?.(error);
          reject(error);
        } else {
          // 正常暂停，保存当前已下载的数据，抛出错误以中断后续流程
          if (xhr.response) {
            task.chunks.push(xhr.response as Blob);
          }
          const error = new Error('下载已暂停');
          reject(error);
        }
      });

      xhr.open('GET', url);

      // 如果是断点续传，设置 Range 头
      if (resumeFrom > 0) {
        xhr.setRequestHeader('Range', `bytes=${resumeFrom}-`);
      }

      xhr.send();
    });
  }

  /**
   * 暂停上传（保存进度用于断点续传）
   */
  pauseUpload(taskId: string): { uploadedBytes: number } | null {
    const task = this.uploadTasks.get(taskId);
    if (task?.xhr) {
      task.aborted = true;
      const uploadedBytes = task.uploadedBytes;
      task.xhr.abort();
      // 不删除任务，保留进度信息
      return { uploadedBytes };
    }
    return null;
  }

  /**
   * 暂停下载（保存进度用于断点续传）
   */
  pauseDownload(
    taskId: string,
  ): { downloadedBytes: number; chunks: Blob[] } | null {
    const task = this.downloadTasks.get(taskId);
    if (task?.xhr) {
      task.aborted = true;
      const downloadedBytes = task.downloadedBytes;
      const chunks = [...task.chunks];
      task.xhr.abort();
      // 不删除任务，保留进度信息
      return { downloadedBytes, chunks };
    }
    return null;
  }

  /**
   * 获取上传任务信息
   */
  getUploadTask(taskId: string): UploadTask | undefined {
    return this.uploadTasks.get(taskId);
  }

  /**
   * 获取下载任务信息
   */
  getDownloadTask(taskId: string): DownloadTask | undefined {
    return this.downloadTasks.get(taskId);
  }

  /**
   * 取消上传
   */
  cancelUpload(taskId: string): boolean {
    const task = this.uploadTasks.get(taskId);
    if (task?.xhr) {
      task.xhr.abort();
      this.uploadTasks.delete(taskId);
      return true;
    }
    return false;
  }

  /**
   * 取消下载
   */
  cancelDownload(taskId: string): boolean {
    const task = this.downloadTasks.get(taskId);
    if (task?.xhr) {
      task.xhr.abort();
      this.downloadTasks.delete(taskId);
      return true;
    }
    return false;
  }
}

export const uploadDownloadManager = new UploadDownloadManager();
